/**
 * Web Vitals Integration
 * Captures and reports Core Web Vitals metrics
 */

import type { WebVitalMetric, PerformanceRating } from '../../domain/types'
import { getWebVitalRating } from '../../domain/constants'
import { analytics } from '../service'

/**
 * Options for Web Vitals tracking
 */
export interface WebVitalsOptions {
  /** Report all changes (not just final values) */
  reportAllChanges?: boolean
  /** Enable debug logging */
  debug?: boolean
  /** Custom callback for metrics */
  onMetric?: (
    metric: WebVitalMetric,
    value: number,
    rating: PerformanceRating
  ) => void
}

/**
 * Web Vitals metric handler type
 */
interface WebVitalsMetric {
  name: string
  value: number
  rating: 'good' | 'needs-improvement' | 'poor'
  navigationType?: string
  attribution?: Record<string, unknown>
}

/**
 * Initialize Web Vitals tracking
 * Call once at app startup (in main.tsx)
 */
export function initWebVitals(options: WebVitalsOptions = {}): void {
  const { reportAllChanges = false, debug = false, onMetric } = options

  // Dynamic import to avoid SSR issues
  if (typeof window === 'undefined') return

  // Note: onFID was removed in web-vitals v4, replaced by onINP
  import('web-vitals').then(({ onCLS, onLCP, onTTFB, onINP, onFCP }) => {
    const handleMetric = (metric: WebVitalsMetric) => {
      const name = metric.name as WebVitalMetric
      const value = metric.value
      const rating = getWebVitalRating(name, value)

      // Track via analytics service
      analytics.trackPerformance({
        metricName: name,
        value,
        rating,
        navigationType: metric.navigationType as
          | 'navigate'
          | 'reload'
          | 'back-forward'
          | 'prerender'
          | undefined,
        attribution: metric.attribution,
      })

      // Call custom callback if provided
      if (onMetric) {
        onMetric(name, value, rating)
      }

      if (debug) {
        const formattedValue =
          name === 'CLS' ? value.toFixed(3) : `${Math.round(value)}ms`
        console.log(`[Web Vitals] ${name}: ${formattedValue} (${rating})`)
      }
    }

    // Register handlers for each metric
    // Note: FID was deprecated in favor of INP (Interaction to Next Paint)
    onLCP(handleMetric, { reportAllChanges })
    onCLS(handleMetric, { reportAllChanges })
    onTTFB(handleMetric, { reportAllChanges })
    onINP(handleMetric, { reportAllChanges })
    onFCP(handleMetric, { reportAllChanges })

    if (debug) {
      console.log('[Web Vitals] Initialized')
    }
  }).catch((error) => {
    console.error('[Web Vitals] Failed to load:', error)
  })
}

/**
 * Manual Web Vital reporting
 * Use when you need to report custom performance metrics
 */
export function reportWebVital(
  metric: WebVitalMetric,
  value: number,
  options?: {
    navigationType?: 'navigate' | 'reload' | 'back-forward' | 'prerender'
    attribution?: Record<string, unknown>
  }
): void {
  const rating = getWebVitalRating(metric, value)

  analytics.trackPerformance({
    metricName: metric,
    value,
    rating,
    navigationType: options?.navigationType,
    attribution: options?.attribution,
  })
}
